import { GoalType } from '@griply/types';
import { isDerivedMetricId } from '@griply/utils';
import { useGoals } from 'features/goals';
import { useLocalizedLifeAreas } from 'features/life-areas';
import React, { useRef, useState } from 'react';
import { GoalContextMenu } from 'shared/components/connected/goal-context-menu';
import { GoalInsightsCard } from 'shared/components/ui/goal-insights-card';
import { ProjectsInsightsCard } from 'shared/components/ui/projects-insights-card';
import { SummaryProgressCard } from 'shared/components/ui/summary-progress-card';
import { useOpenEditGoalDialog } from 'shared/contexts/goal-form';
import { useOpenGoalProgressAddEntry } from 'shared/contexts/goal-progress';
import { useOpenPremiumDialog } from 'shared/contexts/premium-dialog';
import { useContextMenuState } from 'shared/hooks/use-context-menu-state';
import { useEntityLinks } from 'shared/hooks/use-entity-links';
import { useUser } from 'shared/hooks/use-user';
import { getWeekStartsOn } from 'shared/utils/get-week-starts-on';

import { ContextMenu } from './context-menu';

export type InsightsCardProps = Omit<
  React.ComponentProps<typeof GoalInsightsCard>,
  | 'goals'
  | 'lifeAreas'
  | 'links'
  | 'onMenuButton'
  | 'menuButtonRef'
  | 'weekStartsOn'
  | 'onContextMenu'
  | 'onPremium'
  | 'onEditGoalProgress'
> & {
  canUpdateProgress?: boolean;
  isSummaryProgress?: boolean;
};

export const InsightsCard: React.FC<InsightsCardProps> = ({
  goal,
  canUpdateProgress,
  isSummaryProgress,
  ...rest
}) => {
  const contextRef = useRef<HTMLDivElement>(null);
  const menuButtonRef = React.useRef<HTMLButtonElement>(null);
  const user = useUser();
  const lifeAreas = useLocalizedLifeAreas();
  const goals = useGoals();
  const links = useEntityLinks();

  const openPremiumDialog = useOpenPremiumDialog();
  const openProgressAddEntryDialog = useOpenGoalProgressAddEntry();
  const openEditGoalDialog = useOpenEditGoalDialog();

  const [menuButtonUsed, setMenuButtonUsed] = useState(false);
  const {
    open: openMenu,
    close: closeMenu,
    position: menuPosition,
  } = useContextMenuState({
    onClose: () => setMenuButtonUsed(false),
  });

  const openMenuByButton = (e: React.MouseEvent) => {
    setMenuButtonUsed(true);
    openMenu(e);
  };

  const onEditGoalProgress =
    canUpdateProgress &&
    goal.type !== GoalType.Project &&
    goal.metric?.id &&
    !isDerivedMetricId(goal.metric?.id)
      ? () => {
          if (!goal.metric) {
            openEditGoalDialog(goal, goal.id);
            return;
          }

          openProgressAddEntryDialog(goal.id, goal.metric);
        }
      : undefined;

  const weekStartsOn = getWeekStartsOn(user);

  const nonSummaryCardProps = {
    goal,
    goals,
    lifeAreas,
    links,
    onMenuButton: openMenuByButton,
    menuButtonRef,
    weekStartsOn,
    onContextMenu: openMenu,
    onPremium: openPremiumDialog,
    onEditGoalProgress,
    ...rest,
  };

  return (
    <>
      {isSummaryProgress ? (
        <SummaryProgressCard
          goal={goal}
          weekStartsOn={weekStartsOn}
          menuButtonRef={menuButtonRef}
          onMenuButton={openMenuByButton}
          onContextMenu={openMenu}
          onPremium={openPremiumDialog}
          onEditGoalProgress={
            goal.frozenAt || goal.archivedAt ? undefined : onEditGoalProgress
          }
          {...rest}
        />
      ) : goal.type === GoalType.Project ? (
        <ProjectsInsightsCard {...nonSummaryCardProps} />
      ) : (
        <GoalInsightsCard {...nonSummaryCardProps} />
      )}
      {!!menuPosition &&
        (!isSummaryProgress ? (
          <GoalContextMenu
            id={goal.id}
            onClose={closeMenu}
            referenceElement={menuButtonUsed ? menuButtonRef : undefined}
            location={menuButtonUsed ? undefined : menuPosition}
          />
        ) : (
          <ContextMenu
            ref={contextRef}
            goal={goal}
            onClose={closeMenu}
            referenceElement={menuButtonUsed ? menuButtonRef : undefined}
            location={menuButtonUsed ? undefined : menuPosition}
            hideInsightsToggle
          />
        ))}
    </>
  );
};
